import React from 'react';
import { isChunkLoadError, reloadForNewVersion } from '../../utils/lazyWithRetry';

/**
 * The last line under the whole app. Without it, one error thrown while rendering unmounted
 * everything — a white page in production, with no way back but guessing to refresh.
 *
 * A missing code file (the tab is older than the build being served) reloads the page once to
 * pick up the current build; lazyWithRetry normally gets there first, and this catches the rest.
 * Anything else, or a missing file that a reload moments ago didn't fix, gets a plain card that
 * says what happened and has the one thing to do. Drawn on the pre-auth screen's colours so it
 * stands on its own: the app's own styles may be what failed to arrive.
 */
type State = { error: Error | null; reloading: boolean };

export default class AppErrorBoundary extends React.Component<{ children: React.ReactNode; reload?: () => void }, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && reloadForNewVersion(this.props.reload)) this.setState({ reloading: true });
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (reloading) return null;
    const stale = isChunkLoadError(error);
    const reload = this.props.reload || (() => window.location.reload());
    return (
      <div role="alert" className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#f4f1ed] px-6 text-center">
        <img src="/logo.png" alt="" className="h-14 w-auto" />
        <div className="max-w-sm">
          <h1 className="text-xl font-extrabold text-[#1f2a37]">
            {stale ? 'Bearri was just updated' : 'Something went wrong on this page'}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-[#4b5563]">
            {stale
              ? 'This part of Bearri didn’t finish loading. Reload to get the newest version — anything you’ve saved is safe.'
              : 'Reload to try again. Anything you’ve saved is safe. If it keeps happening, email support@bearri.co.'}
          </p>
        </div>
        <button type="button" onClick={reload}
          className="rounded-lg bg-bearriBlue px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:opacity-90">
          Reload
        </button>
      </div>
    );
  }
}
